/**
 * 606 Meta5 AI Trading - Analytics Tab
 * Includes per-strategy breakdown + monthly risk budget tracker.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, TrendingDown, Target, ShieldAlert, Layers, GraduationCap, Trophy, Sparkles } from 'lucide-react';
import { useTradingContext } from '../../context/TradingContext';
import { useTrading } from '../../lib/trading-i18n';
import { fetchStrategyPerformance, fetchSchoolAnalytics } from '../../lib/trading-api';
import StatCard from '../../components/trading/StatCard';
import PnL from '../../components/trading/PnL';
import SmcAnalysisCard from '../../components/trading/SmcAnalysisCard';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const STRATEGY_NAMES = {
  smc: { ar: 'سيولة المؤسسات', en: 'SMC' },
  classic: { ar: 'المؤشرات الفنية', en: 'Classic TA' },
  breakout: { ar: 'اختراق المستويات', en: 'Breakout' },
  trend_following: { ar: 'متابعة الاتجاه', en: 'Trend Following' },
  mean_reversion: { ar: 'الارتداد للمتوسط', en: 'Mean Reversion' },
  scalping: { ar: 'سكالبينج', en: 'Scalping' },
  unknown: { ar: 'غير محدد (سجلات قديمة)', en: 'Unknown (legacy)' },
};

const STRATEGY_COLORS = {
  smc: '#d4af37',
  classic: '#8b5cf6',
  breakout: '#22c55e',
  trend_following: '#3b82f6',
  mean_reversion: '#ec4899',
  scalping: '#f97316',
  unknown: '#71717A',
};

const labelFor = (id, language) => STRATEGY_NAMES[id]?.[language] || id;

const AnalyticsTab = ({ language = 'ar' }) => {
  const t = useTrading(language);
  const { performance, settings } = useTradingContext();
  const [strategyData, setStrategyData] = useState(null);
  const [loadingStrat, setLoadingStrat] = useState(true);
  const [schoolAnalytics, setSchoolAnalytics] = useState(null);
  const [loadingSchools, setLoadingSchools] = useState(true);
  // Selected symbol for the SMC liquidity analysis (defaults to the first user-selected symbol)
  const userSymbols = settings?.symbols && settings.symbols.length > 0
    ? settings.symbols
    : ['XAUUSD'];
  const [smcSymbol, setSmcSymbol] = useState(userSymbols[0]);
  useEffect(() => {
    // If user changes selected symbols and the current SMC symbol is no longer in the list, reset
    if (smcSymbol && !userSymbols.includes(smcSymbol)) {
      setSmcSymbol(userSymbols[0]);
    }
  }, [JSON.stringify(userSymbols), smcSymbol, userSymbols]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await fetchStrategyPerformance();
        if (mounted) setStrategyData(data);
      } catch (e) {
        console.error('Failed to load strategy analytics:', e);
      } finally {
        if (mounted) setLoadingStrat(false);
      }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await fetchSchoolAnalytics(30);
        if (mounted) setSchoolAnalytics(data);
      } catch (e) {
        console.error('Failed to load schools analytics:', e);
      } finally {
        if (mounted) setLoadingSchools(false);
      }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  if (!performance) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const winLossData = [
    { name: t.wins, value: performance.wins, color: '#22c55e' },
    { name: t.losses, value: performance.losses, color: '#ef4444' },
  ];

  const strategyChartData = (strategyData?.breakdown || []).map((row) => ({
    name: labelFor(row.strategy, language),
    pnl: row.net_pnl,
    color: STRATEGY_COLORS[row.strategy] || '#71717A',
    fill: row.net_pnl >= 0 ? '#22c55e' : '#ef4444',
  }));

  const usedPct = Math.min(100, strategyData?.monthly_used_pct || 0);
  const budgetOver = (strategyData?.monthly_used_pct || 0) >= 100;

  return (
    <div className="space-y-6">
      {/* ====== SMC Liquidity Analysis (per selected symbol) ====== */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-5"
        data-testid="smc-liquidity-section"
      >
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#d4af37]" />
            {language === 'ar' ? 'تحليل سيولة المؤسسات (SMC)' : 'Institutional Liquidity Analysis (SMC)'}
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {userSymbols.map((sym) => (
              <button
                key={sym}
                onClick={() => setSmcSymbol(sym)}
                className={`text-[11px] px-2.5 py-1.5 rounded-lg border font-mono transition-colors ${
                  smcSymbol === sym
                    ? 'bg-[#d4af37]/20 border-[#d4af37] text-[#d4af37] font-bold'
                    : 'bg-white/5 border-white/10 text-[#71717A] hover:text-white'
                }`}
                data-testid={`smc-symbol-pick-${sym}`}
              >
                {sym}
              </button>
            ))}
          </div>
        </div>
        <SmcAnalysisCard language={language} symbol={smcSymbol} key={smcSymbol} />
      </motion.div>

      {/* ====== Schools Analytics ====== */}
      {schoolAnalytics && schoolAnalytics.schools.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-[#d4af37]/10 via-transparent to-transparent border border-[#d4af37]/20 rounded-xl p-5"
          data-testid="schools-analytics-card"
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-[#d4af37]" />
              {language === 'ar' ? 'تحليل أداء المدارس (آخر 30 يوم)' : 'Schools Performance (last 30 days)'}
            </h3>
            <span className="text-xs text-[#71717a]">
              {language === 'ar' ? `${schoolAnalytics.total_trades} صفقة مغلقة` : `${schoolAnalytics.total_trades} closed trades`}
            </span>
          </div>

          {schoolAnalytics.best_school && (
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-3">
              <Trophy className="h-5 w-5 text-emerald-400 shrink-0" />
              <div className="text-sm flex-1">
                <span className="text-emerald-400 font-bold">
                  {language === 'ar' ? 'الأفضل أداءً: ' : 'Best performer: '}
                </span>
                <span className="text-white font-semibold">
                  {language === 'ar' ? schoolAnalytics.best_school.name_ar : schoolAnalytics.best_school.name_en}
                </span>
                <span className="text-[#A1A1AA] ms-2">
                  · {language === 'ar' ? 'إجمالي الربح' : 'Total P&L'}: <span className="text-emerald-400 font-bold">+${schoolAnalytics.best_school.total_pnl}</span>
                  · {language === 'ar' ? 'نسبة الفوز' : 'Win rate'}: <span className="font-bold text-white">{schoolAnalytics.best_school.win_rate}%</span>
                  · {schoolAnalytics.best_school.trades} {language === 'ar' ? 'صفقة' : 'trades'}
                </span>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="schools-analytics-table">
              <thead>
                <tr className="text-[#71717a] border-b border-white/5">
                  <th className="text-start py-2 px-2 font-medium">{language === 'ar' ? 'المدرسة' : 'School'}</th>
                  <th className="text-center py-2 px-2 font-medium">{language === 'ar' ? 'صفقات' : 'Trades'}</th>
                  <th className="text-center py-2 px-2 font-medium">{language === 'ar' ? 'فوز' : 'Wins'}</th>
                  <th className="text-center py-2 px-2 font-medium">{language === 'ar' ? 'خسارة' : 'Losses'}</th>
                  <th className="text-center py-2 px-2 font-medium">{language === 'ar' ? 'نسبة الفوز' : 'Win Rate'}</th>
                  <th className="text-end py-2 px-2 font-medium">{language === 'ar' ? 'إجمالي الربح' : 'Total P&L'}</th>
                  <th className="text-end py-2 px-2 font-medium">{language === 'ar' ? 'متوسط' : 'Avg'}</th>
                  <th className="text-end py-2 px-2 font-medium">{language === 'ar' ? 'عامل الربح' : 'PF'}</th>
                </tr>
              </thead>
              <tbody>
                {schoolAnalytics.schools.map((s, idx) => (
                  <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                        <span className="text-white font-medium">{language === 'ar' ? s.name_ar : s.name_en}</span>
                        {idx === 0 && s.total_pnl > 0 && (
                          <Sparkles className="h-3 w-3 text-amber-400" />
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center text-white">{s.trades}</td>
                    <td className="py-2 px-2 text-center text-emerald-400">{s.wins}</td>
                    <td className="py-2 px-2 text-center text-red-400">{s.losses}</td>
                    <td className="py-2 px-2 text-center">
                      <span className={`font-bold ${s.win_rate >= 60 ? 'text-emerald-400' : s.win_rate >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                        {s.win_rate}%
                      </span>
                    </td>
                    <td className={`py-2 px-2 text-end font-bold ${s.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {s.total_pnl >= 0 ? '+' : ''}${s.total_pnl}
                    </td>
                    <td className={`py-2 px-2 text-end ${s.avg_pnl >= 0 ? 'text-emerald-400/80' : 'text-red-400/80'}`}>
                      {s.avg_pnl >= 0 ? '+' : ''}${s.avg_pnl}
                    </td>
                    <td className="py-2 px-2 text-end text-white">{s.profit_factor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {schoolAnalytics.untracked_trades > 0 && (
            <p className="text-[10px] text-[#71717a] mt-3">
              ⓘ {language === 'ar'
                ? `${schoolAnalytics.untracked_trades} صفقة قديمة بدون تتبع المدرسة (قبل تفعيل النظام).`
                : `${schoolAnalytics.untracked_trades} legacy trades without school tracking (from before the system).`}
            </p>
          )}
        </motion.div>
      )}

      {/* Monthly Risk Budget Card */}
      {strategyData && strategyData.monthly_limit_pct > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`bg-white/[0.02] backdrop-blur-xl rounded-xl border p-5 ${
            budgetOver ? 'border-red-500/40' : 'border-[#d4af37]/20'
          }`}
          data-testid="monthly-budget-card"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <ShieldAlert className={`h-5 w-5 ${budgetOver ? 'text-red-400' : 'text-[#d4af37]'}`} />
              {t.monthlyBudget} ({strategyData.month})
            </h3>
            <div className="text-sm text-[#71717A]">
              {t.monthlyPnl}:{' '}
              <span className={strategyData.monthly_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                ${strategyData.monthly_pnl.toFixed(2)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="text-xs text-[#71717A] flex-shrink-0">
              {t.monthlyUsed}: <span className={`font-semibold ${budgetOver ? 'text-red-400' : 'text-white'}`}>{strategyData.monthly_used_pct.toFixed(1)}%</span>
            </div>
            <div className="text-xs text-[#71717A] me-auto">
              / ${strategyData.monthly_budget_usd.toFixed(2)} ({strategyData.monthly_limit_pct}%)
            </div>
          </div>
          <div className="h-3 bg-white/5 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                budgetOver ? 'bg-red-500' : usedPct > 75 ? 'bg-amber-500' : 'bg-[#d4af37]'
              }`}
              style={{ width: `${usedPct}%` }}
            />
          </div>
          {budgetOver && (
            <p className="text-red-400 text-xs mt-3 flex items-center gap-1.5">
              <ShieldAlert className="h-3.5 w-3.5" />
              {t.botAutoStopped} — {t.restartBotManually}.
            </p>
          )}
        </motion.div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title={t.totalTrades} value={performance.total_trades} icon={BarChart3} color="#d4af37" />
        <StatCard title={t.winRate} value={performance.win_rate} suffix="%" icon={Target} color="#22c55e" />
        <StatCard title={t.profitFactor} value={performance.profit_factor} icon={TrendingUp} color={performance.profit_factor >= 1 ? '#22c55e' : '#ef4444'} />
        <StatCard title={t.maxDrawdown} value={performance.max_drawdown_pct} suffix="%" icon={TrendingDown} color="#ef4444" />
      </div>

      {/* More Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <span className="text-[#71717A] text-sm">{t.avgWin}</span>
          <div className="text-xl font-bold text-emerald-400 mt-1">${performance.avg_win.toFixed(2)}</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <span className="text-[#71717A] text-sm">{t.avgLoss}</span>
          <div className="text-xl font-bold text-red-400 mt-1">${performance.avg_loss.toFixed(2)}</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <span className="text-[#71717A] text-sm">{t.closedPnlToday}</span>
          <div className="mt-1"><PnL value={performance.closed_pnl_today} size="xl" /></div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <span className="text-[#71717A] text-sm">{t.totalClosedPnl}</span>
          <div className="mt-1"><PnL value={performance.total_closed_pnl} size="xl" /></div>
        </motion.div>
      </div>

      {/* Charts: Equity Curve + Win/Loss */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <h3 className="text-lg font-semibold text-white mb-4">{t.equityCurve}</h3>
          <div className="h-[250px]">
            {performance.equity_curve.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performance.equity_curve}>
                  <XAxis dataKey="time" hide />
                  <YAxis domain={['auto', 'auto']} hide />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} labelStyle={{ color: '#71717A' }} formatter={(value) => [`$${value.toFixed(2)}`, 'Equity']} />
                  <Line type="monotone" dataKey="equity" stroke="#d4af37" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-[#71717A]">No data available</div>
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <h3 className="text-lg font-semibold text-white mb-4">{t.wins} / {t.losses}</h3>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={winLossData} layout="vertical">
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fill: '#71717A' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {winLossData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Per-Strategy Performance */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-5"
        data-testid="per-strategy-section"
      >
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Layers className="h-5 w-5 text-[#d4af37]" />
          {t.perStrategyAnalysis}
        </h3>

        {loadingStrat ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-6 h-6 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : strategyChartData.length === 0 ? (
          <p className="text-[#71717A] text-center py-10">{t.noStrategyData}</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar Chart */}
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={strategyChartData} margin={{ left: 0, right: 10, top: 10, bottom: 30 }}>
                  <XAxis dataKey="name" tick={{ fill: '#71717A', fontSize: 11 }} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fill: '#71717A', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    formatter={(value) => [`$${Number(value).toFixed(2)}`, t.netPnlCol]}
                    cursor={{ fill: 'rgba(212,175,55,0.06)' }}
                  />
                  <Bar dataKey="pnl" radius={[6, 6, 0, 0]}>
                    {strategyChartData.map((entry, index) => (
                      <Cell key={`cell-strat-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-start py-2 px-2 text-xs font-medium text-[#71717A]">{t.strategyColumn}</th>
                    <th className="text-center py-2 px-2 text-xs font-medium text-[#71717A]">{t.trades}</th>
                    <th className="text-center py-2 px-2 text-xs font-medium text-[#71717A]">{t.winRate}</th>
                    <th className="text-center py-2 px-2 text-xs font-medium text-[#71717A]">{t.netPnlCol}</th>
                  </tr>
                </thead>
                <tbody>
                  {strategyData.breakdown.map((row) => (
                    <tr key={row.strategy} className="border-b border-white/5" data-testid={`strategy-row-${row.strategy}`}>
                      <td className="py-2.5 px-2">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STRATEGY_COLORS[row.strategy] || '#71717A' }} />
                          <span className="text-white">{labelFor(row.strategy, language)}</span>
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-center text-white">
                        {row.trades}
                        <span className="text-[10px] text-[#71717A] ms-1">
                          ({row.wins}✓/{row.losses}✗)
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <span className={row.win_rate >= 50 ? 'text-emerald-400' : 'text-red-400'}>
                          {row.win_rate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <PnL value={row.net_pnl} size="sm" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AnalyticsTab;
